import { url, os, language, user } from '@tencent/ppd-base-libs';
import { WebBi } from '@tencent/bi-base-sdk';
import { isInWeixin } from '@tencent/wphk-components-common/utils/ua';

let bi = null as unknown as InstanceType<typeof WebBi>;
let globalOptions = {} as any;

export enum MtnBiReportPageType {
  RedirectPage = 'redirect_page',
  NormalPage = 'page',
}

export enum MtnBiReportReportType {
  SendBeacon = 'sendBeacon',
}

/**
 * 初始化
 */
export function initMtnBiReport(options?: {
  /**
   * channelId
   * 通过process.env.VUE_APP_PKGNAME传入
   */
  channel?: string;
  /**
   * 上报模式，不传使用默认上报模式
   */
  reportType?: MtnBiReportReportType;
  /**
   * 是否必须登陆，如果没有从cookie中拿到登陆信息，则不会上报
   * 在活动页，当用户没有登陆的情况下，大部份时候会跳转去登陆，登陆回来之后，就会带上登陆态
   * 通过needLogin可以控制，只有当用户登陆后，才会执行这次上报，
   * 未登陆就不上报
   */
  needLogin?: boolean;
  /**
   * 当前页面类型，redirect_page: 中转页；page: 落地页（默认）
   * 传不同的pageType，会让上报的事件名进行切换
   */
  pageType?: MtnBiReportPageType;
  /**
   * 开启调试，输出调试信息
   */
  debug?: boolean;
}) {
  globalOptions = options || {};

  const localOptions = {
    sid: '500008935',
    cid: '500009774',
  };

  if (globalOptions.reportType === 'sendBeacon') {
    Object.assign(localOptions, {
      isUseBeacon: true,
      mtaUrl: 'https://hq-tracking-pay.qq.com/report/minipro/v2?r2=500009774',
    });
  }

  // @ts-ignore
  bi = new WebBi(localOptions);
  return bi;
}

/**
 * 清理内存
 */
export function clearMtnBiReport() {
  // @ts-ignore
  bi = null;
  globalOptions = null;
}

/**
 * 上报
 */
export function mtnBiReport(ext?: any) {
  try {
    if (!bi) {
      return;
    }
    // 如果url中不存在mtn，则直接忽略上报
    if (!getMtn()) {
      return;
    }
    // 未登陆的情况下不上报
    if (globalOptions.needLogin && !user.getUin()) {
      return;
    }

    const baseParams = getMtnBiReportParams();
    const params = { ...baseParams, ...(ext || {}) };
    const event = `prom.mtn.${globalOptions.pageType || 'page'}_brow`;

    if (globalOptions.debug) {
      console.debug('%c== MtnBiReport ==', 'color:red', event, params);
    }

    // @ts-ignore
    bi.eventStat(event, params);
  } catch (e) {};
}

/**
 * 拿到上报信息
 * @returns
 */
export function getMtnBiReportParams() {
  const fmtn = getMtn();

  const osInfo = os.getOSInfoObj();

  const { href, host, hash } = window.location;
  const fhost = host;
  const fpathname = hash.substring(1) || url.getUrlParam('page');
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const fchannel_id = getChannel();
  const fos = osInfo.type;
  const fosver = osInfo.version;
  const fclientver = os.getWeixinVer();
  const flanguage = (language.getLanguage() || '').replace('-', '_');
  const fsource = isInWeixin() ? 'inwx' : 'thirdapp';
  const ftime = Date.now();
  const furl = href;

  const fuin = user.getUin() || getUid();
  const fopenid = user.getFuin() || getUid();
  const fvin = getVin();

  const fadtag = getAdtag();
  const fcampaignid = getCampaignId();
  const fbatchid = getBatchId();
  const finorex = getInorex();
  const ftargeturl = getTargetUrl();
  const fconfid = getConfId();

  return {
    fmtn,
    fhost,
    fpathname,
    fchannel_id,
    furl,
    fos,
    fosver,
    fclientver,
    flanguage,
    fsource,
    ftime,
    fuin,
    fopenid,
    fvin,
    fadtag,
    fcampaignid,
    fbatchid,
    finorex,
    ftargeturl,
    fconfid,
  };
}

/**
 * 拿到当前用户的vin
 * @returns
 */
function getVin(): string {
  const urlvin = url.getUrlParam('vin') || url.getUrlParam('VIN');
  if (urlvin) {
    return urlvin;
  }

  const storagevin = localStorage.getItem('MTN_BI_REPORT_VIN');
  if (storagevin) {
    return storagevin;
  }

  const vin = (() => {
    const chars = 'abcdefjkijklmnopqrstuvwxyz';
    const max = chars.length;
    let vin = '';
    for (let i = 0; i < 16; i ++) {
      vin += chars.charAt(Math.floor(Math.random() * max));
    }
    return vin;
  })();
  localStorage.setItem('MTN_BI_REPORT_VIN', vin);
  return vin;
}

/**
 * 生成用户Id
 */
function getUid(): string {
  const vin = getVin();
  const prefix = isInWeixin() ? 'inwx' : 'thirdapp';
  return `${prefix}_${vin}@wx.hkg`;
}

function getMtn(): string {
  const mtn = url.getUrlParam('MTN') || url.getUrlParam('mtn') || '';
  return mtn;
}

function getCampaignId(): string {
  const campid = url.getUrlParam('campaignid') || '';
  return campid;
}

function getBatchId() {
  const bid = url.getUrlParam('batchid') || '';
  return bid;
}

function getInorex() {
  const urlinorex = url.getUrlParam('INOREX') || url.getUrlParam('inorex');
  if (urlinorex) {
    return urlinorex;
  }
  return isInWeixin() ? '1' : '2';
}

function getAdtag() {
  const adtag = url.getUrlParam('ADTAG') || url.getUrlParam('adtag') || '';
  return adtag;
}

function getTargetUrl() {
  const targeturl = url.getUrlParam('targeturl') || '';
  const turl = decodeURIComponent(targeturl);
  return turl;
}

function getConfId() {
  const cid = url.getUrlParam('confId') || '';
  return cid;
}

function getChannel() {
  if (globalOptions.channel) {
    return globalOptions.channel;
  }
  const { pathname } = window.location;
  const [, channel] = pathname.split('/');
  return channel;
}
